// src/components/inventory/InventoryMenuSampleOrderBuilder.js
// RULES-FIRST: Everything visible can be added to cart
// Grayed-out items (NOT sampleable) can be seen but not added
//
// ✅ UPDATE INCLUDED:
// - Review button navigates to SampleOrdersScreen and pre-fills cartItems
// - Category dropdown filter (All / per-category)
// ✅ NEW: Tile ordering puts sampleable items FIRST, non-sampleable LAST

import React, { useMemo, useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  getFirestore,
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import {
  ShoppingCart,
  Plus,
  Minus,
  Trash2,
  ArrowRight,
  Search,
  Maximize2,
  Minimize2,
  Image as ImageIcon,
  Loader2,
  Filter,
  Pencil,
} from "lucide-react";

import useSampleOrderCart from "../../hooks/useSampleOrderCart";
import InventoryEditPanel from "./InventoryEditPanel";

const normalizeStr = (v) => (v || "").toString().trim().toLowerCase();
const safeArray = (v) => (Array.isArray(v) ? v : []);
const uniqStrings = (arr) =>
  Array.from(new Set(safeArray(arr).filter(Boolean).map(String)));

const chunk = (arr, size) => {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
};

const pickImageUrl = (inv) => {
  return (
    inv?.imageUrl ||
    inv?.imageURL ||
    inv?.photoUrl ||
    inv?.photoURL ||
    inv?.image ||
    inv?.thumbnailUrl ||
    inv?.thumbnailURL ||
    inv?.thumbUrl ||
    inv?.thumbURL ||
    ""
  );
};

// ✅ sort helper: sampleable first, then name
const sortSampleableFirstByName = (a, b) => {
  const aNot = a?.sampleable !== true;
  const bNot = b?.sampleable !== true;

  if (aNot !== bNot) return aNot ? 1 : -1; // sampleable first

  const an = (a?.name || "").toString().toLowerCase();
  const bn = (b?.name || "").toString().toLowerCase();
  return an.localeCompare(bn);
};

export default function InventoryMenuSampleOrderBuilder({
  companyId,
  currentUserId,
  inventoryItems = null,
  onInventoryUpdate = null,
  // ✅ default to SampleOrdersScreen (Create tab)
  goToOrderPath = "/sample-orders?view=create",
}) {
  const navigate = useNavigate();
  const db = getFirestore();
  const auth = getAuth();

  const cart = useSampleOrderCart({ companyId, currentUserId });

  const [me, setMe] = useState(null);
  const [loadingMe, setLoadingMe] = useState(true);

  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState("");
  const [showOnlySelected, setShowOnlySelected] = useState(false);
  const [gridMode, setGridMode] = useState("collapse");
  const [editingItem, setEditingItem] = useState(null);
  const [isEditOpen, setIsEditOpen] = useState(false);

  // ✅ dropdown category filter
  const [categoryFilter, setCategoryFilter] = useState("all");

  const role = useMemo(
    () => (me?.role || "").toString().trim().toLowerCase(),
    [me]
  );

  const userGroupIds = useMemo(() => uniqStrings(me?.groupIds), [me]);

  const isCustomer = role === "customer";
  const isOwnerAdmin = role === "owner" || role === "admin" || role === "super_admin";
  const isEndUser = role === "enduser" || role === "end_user";
  const isStaff = isOwnerAdmin || isEndUser;

  useEffect(() => {
    const loadMe = async () => {
      const uid = currentUserId || auth.currentUser?.uid || null;
      if (!uid) {
        setMe(null);
        setLoadingMe(false);
        return;
      }

      try {
        setLoadingMe(true);
        const snap = await getDoc(doc(db, "users", uid));
        if (snap.exists()) setMe({ uid, id: snap.id, ...snap.data() });
        else setMe({ uid });
      } catch (e) {
        console.error("[InventoryMenuSampleOrderBuilder] failed to load user doc:", e);
        setMe({ uid: currentUserId || auth.currentUser?.uid || null });
      } finally {
        setLoadingMe(false);
      }
    };

    loadMe();
  }, [auth, db, currentUserId]);

  const buildInventoryQueries = useCallback(() => {
    if (!companyId || loadingMe) return [];

    const inventoryRef = collection(db, "companies", companyId, "inventory");

    // Customers: customerVisible items only (rules enforce)
    if (isCustomer) {
      return [query(inventoryRef, where("customerVisible", "==", true))];
    }

    // Owner/Admin: all inventory
    if (isOwnerAdmin) {
      return [query(inventoryRef)];
    }

    // End user: groupIds overlap (chunked for >10 groups)
    if (isEndUser) {
      const gids = uniqStrings(userGroupIds);
      if (!gids.length) return [];
      return chunk(gids, 10).map((c) =>
        query(inventoryRef, where("groupIds", "array-contains-any", c))
      );
    }

    return [];
  }, [companyId, db, isCustomer, isOwnerAdmin, isEndUser, userGroupIds, loadingMe]);

  const loadInventory = useCallback(async () => {
    if (!companyId) return;

    if (Array.isArray(inventoryItems)) {
      const items = inventoryItems
        .filter((item) => item?.archived !== true)
        .slice()
        .sort(sortSampleableFirstByName);
      setInventory(items);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const qs = buildInventoryQueries();

      if (!qs.length) {
        setInventory([]);
        setError(isCustomer ? "No products available." : "Unable to load catalog.");
        setLoading(false);
        return;
      }

      const snaps = await Promise.all(qs.map((qRef) => getDocs(qRef)));

      const byId = new Map();
      snaps.forEach((snap) => {
        snap.docs.forEach((d) => byId.set(d.id, { id: d.id, ...d.data() }));
      });

      let items = Array.from(byId.values());
      items = items.filter((x) => x?.archived !== true);

      if (isCustomer) {
        items = items.filter((x) => x?.customerVisible === true);
      }

      // ✅ sampleable first, then name
      items.sort(sortSampleableFirstByName);

      console.log(`[InventoryMenuSampleOrderBuilder] Loaded ${items.length} items as ${role}`);
      setInventory(items);

      // ✅ If current selected category no longer exists, reset to all
      const existingCats = new Set(
        items.map((x) => (x?.category || "").toString().trim()).filter(Boolean)
      );
      if (categoryFilter !== "all" && !existingCats.has(categoryFilter)) {
        setCategoryFilter("all");
      }
    } catch (err) {
      console.error("[InventoryMenuSampleOrderBuilder] Error loading inventory:", err);

      if (err?.code === "permission-denied") {
        setError("You don't have permission to view the catalog.");
      } else {
        setError("Failed to load catalog. Please try again.");
      }

      setInventory([]);
    } finally {
      setLoading(false);
    }
  }, [companyId, buildInventoryQueries, inventoryItems, isCustomer, role, categoryFilter]);

  useEffect(() => {
    if (!companyId) return;
    if (loadingMe) return;
    loadInventory();
  }, [companyId, loadingMe, loadInventory]);

  // ✅ categories for dropdown
  const categoryOptions = useMemo(() => {
    const cats = uniqStrings(inventory.map((p) => (p?.category || "").toString().trim()));
    const sorted = cats.sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
    return ["all", ...sorted];
  }, [inventory]);

  const filtered = useMemo(() => {
    const s = normalizeStr(search);
    let list = Array.isArray(inventory) ? inventory : [];

    // category dropdown filter
    if (categoryFilter !== "all") {
      list = list.filter((p) => String(p?.category || "").trim() === categoryFilter);
    }

    if (s) {
      list = list.filter((p) => {
        const name = normalizeStr(p?.name);
        const sku = normalizeStr(p?.sku);
        const category = normalizeStr(p?.category);
        return name.includes(s) || sku.includes(s) || category.includes(s);
      });
    }

    if (showOnlySelected) {
      list = list.filter((p) => (cart?.getQty?.(p.id) || 0) > 0);
    }

    // ✅ sampleable first, then name
    return [...list].sort(sortSampleableFirstByName);
  }, [inventory, search, showOnlySelected, cart, categoryFilter]);

  const gridClass =
    gridMode === "collapse"
      ? "grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-3"
      : "grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3";

  const imageHeightClass = gridMode === "collapse" ? "h-28" : "h-40";

  const distinctCount = cart?.distinctCount || 0;
  const totalUnits = cart?.totalUnits || 0;
  const hasCart = distinctCount > 0;

  const removeItem = (id) => {
    if (typeof cart?.remove === "function") return cart.remove(id);
    if (typeof cart?.setQty === "function") return cart.setQty(id, 0);
  };

  const handleEditClick = (item) => {
    if (!isOwnerAdmin) return;
    setEditingItem(item);
    setIsEditOpen(true);
  };

  const handleEditClose = () => {
    setIsEditOpen(false);
    setEditingItem(null);
  };

  // ✅ Review should go to SampleOrdersScreen and prefill the form
  const handleReview = () => {
    if (!hasCart) return;

    // Use the hook cart as the source of truth
    const cartItems = (cart?.itemsArray || [])
      .map((i) => ({
        productId: i.productId,
        quantity: Number(i.quantity) || 1,
      }))
      .filter((x) => x.productId && Number(x.quantity) > 0);

    navigate(goToOrderPath, { state: { cartItems } });
  };

  if (loadingMe || loading) {
    return (
      <div className="flex items-center justify-center py-10">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto" />
          <p className="mt-3 text-sm text-gray-600">Loading catalog...</p>
        </div>
      </div>
    );
  }

  if (!isCustomer && !isStaff) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-5 text-sm text-slate-700">
        Your role does not have access to the catalog.
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-5">
        <div className="flex items-start gap-3">
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-red-800">Error</h3>
            <p className="mt-1 text-sm text-red-700">{error}</p>

            <div className="mt-3">
              <button
                type="button"
                onClick={loadInventory}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white border border-red-200 text-red-700 hover:bg-red-50 text-sm font-semibold"
              >
                <Loader2 className="w-4 h-4" />
                Try again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
    <div className="space-y-2">
          <div className="sticky top-0 z-30 -mx-1 border-b border-slate-200 bg-white/95 px-1 py-2 shadow-sm backdrop-blur">
      <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
        {/* Left controls */}
        <div className="flex flex-row gap-2 flex-1">
          {/* Search */}
          <div className="relative min-w-0 flex-1">
            <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search products..."
              className="w-full pl-8 pr-3 py-1.5 text-sm rounded-lg border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Category dropdown */}
          <div className="relative w-[46%] min-w-[150px]">
            <Filter className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="w-full pl-8 pr-8 py-1.5 text-sm rounded-lg border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none"
              title="Filter by category"
            >
              {categoryOptions.map((c) => (
                <option key={c} value={c}>
                  {c === "all" ? "All categories" : c}
                </option>
              ))}
            </select>
            <div className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400">
              ▾
            </div>
          </div>
        </div>

        {/* Right controls */}
        <div className="flex flex-wrap items-center justify-between lg:justify-end gap-2">
          <label className="inline-flex items-center gap-1.5 text-xs text-slate-700 select-none cursor-pointer">
            <input
              type="checkbox"
              checked={showOnlySelected}
              onChange={(e) => setShowOnlySelected(e.target.checked)}
              className="rounded border-slate-300 w-3.5 h-3.5"
            />
            Selected
          </label>

          <button
            type="button"
            onClick={() => setGridMode((m) => (m === "expand" ? "collapse" : "expand"))}
            className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-xs font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
            title={gridMode === "expand" ? "Collapse grid" : "Expand grid"}
          >
            {gridMode === "expand" ? (
              <Minimize2 className="w-4 h-4" />
            ) : (
              <Maximize2 className="w-4 h-4" />
            )}
            <span className="hidden sm:inline">
              {gridMode === "expand" ? "Collapse" : "Expand"}
            </span>
          </button>

          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-slate-200 bg-white">
            <ShoppingCart className="w-4 h-4 text-blue-600" />
            <span className="text-xs font-semibold text-slate-900">{distinctCount}</span>
            <span className="text-xs text-slate-400">•</span>
            <span className="text-xs text-slate-600">{totalUnits}</span>
          </div>

          <button
            type="button"
            onClick={handleReview}
            disabled={!hasCart}
            className="inline-flex items-center justify-center gap-1.5 px-4 py-1.5 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Review
            <ArrowRight className="w-3.5 h-3.5" />
          </button>

          <button
            type="button"
            onClick={() => (typeof cart?.clear === "function" ? cart.clear() : null)}
            disabled={!hasCart}
            className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-slate-700 text-sm font-semibold hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            title="Clear cart"
          >
            <Trash2 className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Clear</span>
          </button>
        </div>
      </div>
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-8 text-center text-sm text-slate-600">
          {inventory.length === 0 ? "No products available." : "No products match your filters."}
        </div>
      ) : (
        <div className={gridClass}>
          {filtered.map((p) => {
            const qty = Number(cart?.getQty?.(p.id) || 0);
            const name = p?.name || "Untitled";
            const sku = p?.sku ? String(p.sku) : "";
            const category = p?.category ? String(p.category) : "";
            const imageUrl = pickImageUrl(p);

            // Not sampleable = grayed out but visible
            const notSampleable = p?.sampleable !== true;

            return (
              <div
                key={p.id}
                className={`border rounded-xl shadow-sm transition-all overflow-hidden flex flex-col ${
                  notSampleable ? "opacity-60" : ""
                } ${
                  qty > 0
                    ? "border-blue-200 bg-blue-50/80 ring-1 ring-blue-100 shadow-md"
                    : "bg-white border-slate-200 hover:border-slate-300"
                }`}
              >
                <div
                  className={`relative w-full ${imageHeightClass} overflow-hidden border-b border-slate-100 bg-slate-50`}
                >
                  {imageUrl ? (
                    <img
                      src={imageUrl}
                      alt={name}
                      className="w-full h-full object-cover object-center"
                      loading="lazy"
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-slate-300">
                      <ImageIcon className="w-8 h-8" />
                    </div>
                  )}

                  <div className="absolute top-2 right-2 flex items-center gap-2">
                    {isOwnerAdmin ? (
                      <button
                        type="button"
                        onClick={() => handleEditClick(p)}
                        className="inline-flex items-center gap-1 rounded-md bg-white/95 px-2 py-1 text-[11px] font-semibold text-slate-700 border border-slate-200 hover:bg-white"
                        title="Edit product"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                        <span className="hidden sm:inline">Edit</span>
                      </button>
                    ) : null}

                    {qty > 0 && (
                      <div className="rounded-md bg-blue-50 text-blue-700 border border-blue-100 px-2 py-0.5 text-[11px] font-semibold">
                        Qty: {qty}
                      </div>
                    )}
                  </div>

                  {notSampleable && (
                    <div className="absolute top-2 left-2 rounded-md bg-amber-50 text-amber-800 border border-amber-200 px-2 py-0.5 text-[11px] font-semibold">
                      Not Available
                    </div>
                  )}
                </div>

                <div className="p-2.5 flex-1 min-w-0">
                  <div className="text-xs font-semibold text-slate-900 line-clamp-2 leading-tight">
                    {name}
                  </div>

                  {(sku || category) && (
                    <div className="text-[10px] text-slate-500 mt-0.5 truncate">
                      {sku && <span>SKU: {sku}</span>}
                      {sku && category && <span> • </span>}
                      {category && <span>{category}</span>}
                    </div>
                  )}
                </div>

                <div className="px-2.5 pb-2.5 pt-2 border-t border-slate-100">
                  <div className="flex items-center justify-between gap-2">
                    {notSampleable ? (
                      <div className="text-[11px] text-amber-700 font-medium">
                        Currently unavailable
                      </div>
                    ) : qty > 0 ? (
                      <>
                        <div className="inline-flex items-center rounded-lg border border-gray-200 bg-white overflow-hidden">
                          <button
                            type="button"
                            onClick={() => cart?.setQty?.(p.id, Math.max(0, qty - 1))}
                            className="h-8 w-8 flex items-center justify-center hover:bg-gray-50"
                            title="Decrease"
                          >
                            <Minus className="w-4 h-4 text-gray-700" />
                          </button>

                          <div className="min-w-[38px] h-8 flex items-center justify-center text-sm font-semibold text-gray-900 border-x border-gray-200">
                            {qty}
                          </div>

                          <button
                            type="button"
                            onClick={() => cart?.add?.(p.id, 1)}
                            className="h-8 w-8 flex items-center justify-center hover:bg-gray-50"
                            title="Increase"
                          >
                            <Plus className="w-4 h-4 text-gray-700" />
                          </button>
                        </div>

                        <button
                          type="button"
                          onClick={() => removeItem(p.id)}
                          className="h-8 px-2.5 inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 text-xs font-semibold"
                          title="Remove from order"
                        >
                          <Trash2 className="w-4 h-4" />
                          <span className="hidden sm:inline">Remove</span>
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        onClick={() => cart?.add?.(p.id, 1)}
                        className="inline-flex items-center gap-2 h-8 px-3 rounded-lg bg-blue-100 text-blue-800 border border-blue-200 text-xs font-semibold hover:bg-blue-200"
                      >
                        <ShoppingCart className="w-4 h-4" />
                        Add
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
      <InventoryEditPanel
        isOpen={isEditOpen}
        onClose={handleEditClose}
        companyId={companyId}
        item={editingItem}
        onUpdated={() => {
          handleEditClose();
          if (typeof onInventoryUpdate === "function") {
            onInventoryUpdate();
            return;
          }
          loadInventory();
        }}
      />
    </>
  );
}
